import { z } from "zod";

/** Botox / filler / mesotherapy … — mirrors the DB injectable_type enum. */
export const injectableTypeSchema = z.enum([
	"botox",
	"filler",
	"mesotherapy",
	"pdo_thread",
	"prp",
	"other",
]);
export type InjectableType = z.infer<typeof injectableTypeSchema>;

/** Named face/neck injection sites — mirrors the DB injection_region enum. */
export const injectionRegionSchema = z.enum([
	"forehead",
	"glabella",
	"crows_feet",
	"brow",
	"nose",
	"cheeks",
	"tear_trough",
	"nasolabial",
	"lips",
	"marionette",
	"chin",
	"jawline",
	"masseter",
	"temples",
	"neck",
	"other",
]);
export type InjectionRegion = z.infer<typeof injectionRegionSchema>;

// ---- Lots (inventory + recall unit) ----

export const injectableLotSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	product_name: z.string(),
	type: injectableTypeSchema,
	lot_number: z.string().nullable(),
	manufacturer: z.string().nullable(),
	country_of_origin: z.string().nullable(),
	expiry_date: z.string().nullable(),
	unit_label: z.string(),
	total_units: z.number(),
	units_remaining: z.number(),
	cost_per_unit: z.number().nullable(),
	currency: z.string(),
	received_at: z.string().datetime(),
	recorded_by_member_id: z.string().uuid().nullable(),
	notes: z.string().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type InjectableLotApi = z.infer<typeof injectableLotSchema>;

export const createInjectableLotInputSchema = z.object({
	product_name: z.string().min(1).max(160),
	type: injectableTypeSchema.default("botox"),
	lot_number: z.string().max(120).optional(),
	manufacturer: z.string().max(160).optional(),
	country_of_origin: z.string().max(120).optional(),
	expiry_date: z.string().optional(),
	unit_label: z.string().max(16).default("U"),
	total_units: z.number().nonnegative().default(0),
	cost_per_unit: z.number().nonnegative().nullable().optional(),
	currency: z.string().max(8).default("IQD"),
	notes: z.string().max(1000).optional(),
});
export type CreateInjectableLotInput = z.infer<
	typeof createInjectableLotInputSchema
>;

export const updateInjectableLotInputSchema = z.object({
	product_name: z.string().min(1).max(160).optional(),
	type: injectableTypeSchema.optional(),
	lot_number: z.string().max(120).nullable().optional(),
	manufacturer: z.string().max(160).nullable().optional(),
	country_of_origin: z.string().max(120).nullable().optional(),
	expiry_date: z.string().nullable().optional(),
	unit_label: z.string().max(16).optional(),
	/** Manual stock correction. */
	units_remaining: z.number().nonnegative().optional(),
	cost_per_unit: z.number().nonnegative().nullable().optional(),
	notes: z.string().max(1000).nullable().optional(),
});
export type UpdateInjectableLotInput = z.infer<
	typeof updateInjectableLotInputSchema
>;

// ---- Injection records (per-patient treatment events) ----

export const injectionRecordSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().nullable(),
	lot_id: z.string().uuid().nullable(),
	product_name: z.string(),
	lot_number: z.string().nullable(),
	type: injectableTypeSchema,
	region: injectionRegionSchema,
	units: z.number(),
	injected_at: z.string().datetime(),
	performed_by_member_id: z.string().uuid().nullable(),
	performed_by_name: z.string().nullable(),
	notes: z.string().nullable(),
	/** Amount charged to the patient for this injection (null = not billed yet). */
	patient_charge: z.number().nullable(),
	/** The invoice this injection was billed on (null = no charge / billed manually). */
	invoice_id: z.string().uuid().nullable(),
	created_at: z.string().datetime(),
});
export type InjectionRecordApi = z.infer<typeof injectionRecordSchema>;

export const createInjectionRecordInputSchema = z.object({
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().optional(),
	/** When given, the lot's remaining units are decremented and its
	 *  product/lot snapshotted onto the record. Omit for a free-text record. */
	lot_id: z.string().uuid().optional(),
	/** Required only when no lot_id (free-text product). */
	product_name: z.string().max(160).optional(),
	type: injectableTypeSchema.default("botox"),
	region: injectionRegionSchema,
	// Defensive ceiling: a single injection is at most a few hundred units (botox)
	// or a few ml (filler). 1000 catches a data-entry typo (e.g. 5000U) before it
	// lands in permanent treatment history. Clinical doses are well under this.
	units: z.number().positive().max(1000),
	injected_at: z.string().optional(),
	notes: z.string().max(1000).optional(),
	/** Optional: charge the patient for this injection. When > 0, the server
	 *  creates a linked invoice (transactionally) so a core revenue procedure is
	 *  billable + traceable instead of being cost-of-goods only. Omit to record
	 *  the injection without a charge (bill manually / batch later). */
	patient_charge: z.number().nonnegative().max(100_000_000).optional(),
});
export type CreateInjectionRecordInput = z.infer<
	typeof createInjectionRecordInputSchema
>;

/** Recall lookup: a lot + every patient who received an injection from it. */
export const injectableRecallSchema = z.object({
	lot: injectableLotSchema,
	affected: z.array(
		z.object({
			patient_id: z.string().uuid(),
			patient_name: z.string(),
			injection_count: z.number(),
			last_injected_at: z.string().datetime(),
		}),
	),
});
export type InjectableRecallApi = z.infer<typeof injectableRecallSchema>;
